import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import moment from 'moment'

import {
  useIntegrationProviders,
  useUserIntegrations
} from 'global/hooks/useIntegrations'
import { useUserFeatureFlags } from 'global/hooks/useFeatureFlags'
import { useUserTranscripts } from 'global/hooks/useTranscripts'
import {
  useCreateLeadershipProfile,
  useLeadershipProfile,
  useUserLeadershipProfiles
} from 'global/hooks/useLeadershipProfiles'
import { FEATURE_FLAG_KEYS } from 'global/hooks/useFeatureFlags/constants'

import Button from 'components/Button'

import GenerationProgress from 'pages/Member/LeadershipInAction/GenerationProgress'
import ProfileDetail from 'pages/Member/LeadershipInAction/ProfileDetail'
import ProfileCard from 'pages/Member/LeadershipInAction/ProfileCard'
import GenerateProfileModal from 'pages/Member/LeadershipInAction/GenerateProfileModal'

const buildAccountLine = metadata => {
  if (!metadata) return null
  const { accountName, accountEmail } = metadata
  if (accountName && accountEmail) {
    return `${accountName} (${accountEmail})`
  }
  return accountName || accountEmail || null
}

const LeadershipInAction = () => {
  const { userId } = useParams()
  const {
    providers,
    isLoading: providersLoading
  } = useIntegrationProviders()
  const {
    integrations,
    isLoading: integrationsLoading
  } = useUserIntegrations(userId)
  const { flags, isLoading: flagsLoading } = useUserFeatureFlags(
    userId
  )

  const connectedProviders = providers.filter(p =>
    integrations.some(i => i.provider === p.key)
  )
  const activeProvider = connectedProviders[0] || null
  const activeIntegration = activeProvider
    ? integrations.find(i => i.provider === activeProvider.key)
    : null

  const liaFlag = flags.find(
    f => f.key === FEATURE_FLAG_KEYS.LEADERSHIP_IN_ACTION
  )
  const isFeatureEnabled = liaFlag ? liaFlag.effective : false

  const {
    transcripts,
    isLoading: transcriptsLoading,
    error: transcriptsError,
    refetch: refetchTranscripts
  } = useUserTranscripts(
    userId,
    activeProvider ? activeProvider.key : null,
    { enabled: Boolean(activeProvider) }
  )

  const [activeProfileId, setActiveProfileId] = useState(null)
  const [viewingProfileId, setViewingProfileId] = useState(null)
  const [showGenerateModal, setShowGenerateModal] = useState(
    false
  )

  const {
    create,
    isCreating,
    createError,
    reset: resetCreate
  } = useCreateLeadershipProfile({ userId })

  const {
    profiles,
    isLoading: profilesLoading
  } = useUserLeadershipProfiles(userId)

  // If the coach navigates away while a profile is generating and comes
  // back, re-attach to any in-flight profile so they see the progress bar
  // instead of just a "Generating" card in the grid.
  useEffect(() => {
    if (activeProfileId) return
    if (viewingProfileId) return
    const inFlight = (profiles || []).find(
      p => p.status === 'queued' || p.status === 'running'
    )
    if (inFlight) setActiveProfileId(inFlight.id)
  }, [profiles, activeProfileId, viewingProfileId])

  const {
    profile: viewingProfile,
    isLoading: viewingProfileLoading
  } = useLeadershipProfile(viewingProfileId, {
    pollIntervalMs: 0,
    enabled: Boolean(viewingProfileId)
  })

  const openGenerateModal = () => {
    resetCreate()
    setShowGenerateModal(true)
  }
  const closeGenerateModal = () => {
    setShowGenerateModal(false)
  }

  const handleGenerate = async transcriptIds => {
    if (!activeProvider) return
    const profile = await create({
      provider: activeProvider.key,
      transcriptIds
    })
    if (profile && profile.id) {
      setShowGenerateModal(false)
      setActiveProfileId(profile.id)
    }
  }

  const handleStartOver = () => {
    setActiveProfileId(null)
    resetCreate()
  }

  const handleViewProfile = id => {
    setActiveProfileId(null)
    setViewingProfileId(id)
  }

  const handleBackToList = () => {
    setViewingProfileId(null)
  }

  if (providersLoading || integrationsLoading || flagsLoading) {
    return <p>Loading…</p>
  }

  if (viewingProfileId) {
    return (
      <div>
        {viewingProfileLoading || !viewingProfile ? (
          <p>Loading profile…</p>
        ) : (
          <ProfileDetail
            profile={viewingProfile}
            onBack={handleBackToList}
          />
        )}
      </div>
    )
  }

  const hasIntegration = Boolean(activeProvider)
  const hasProfiles = (profiles || []).length > 0

  const renderConnectionFooter = () => {
    const accountLine = buildAccountLine(
      activeIntegration && activeIntegration.metadata
    )
    const lastValidatedLabel =
      activeIntegration && activeIntegration.lastValidatedAt
        ? `Last validated ${moment(
            activeIntegration.lastValidatedAt
          ).fromNow()}`
        : null

    if (hasIntegration) {
      return (
        <div
          className="border rounded mt-30 px-20 py-15 bg-gray-light"
          style={{ fontSize: 14 }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#34C77B',
              marginRight: 8,
              verticalAlign: 'middle'
            }}
          />
          Connected: {activeProvider.name}
          {accountLine ? ` (${accountLine})` : ''}
          {lastValidatedLabel ? ` · ${lastValidatedLabel}` : ''}
        </div>
      )
    }
    return (
      <div
        className="border rounded mt-30 px-20 py-15 bg-gray-light"
        style={{ fontSize: 14 }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#B5BEC6',
            marginRight: 8,
            verticalAlign: 'middle'
          }}
        />
        No transcript source connected. The client connects a
        source themselves from their Leadership in Action page.
      </div>
    )
  }

  const renderEmptyState = () => {
    if (!isFeatureEnabled) {
      return (
        <div className="p-30 border rounded bg-gray-light text-center">
          <p
            className="font-weight-bold mb-10"
            style={{ fontSize: 18 }}
          >
            Feature not enabled for this client
          </p>
          <p
            className="text-gray-dark mb-0"
            style={{ maxWidth: 540, margin: '0 auto' }}
          >
            Enable the &quot;Leadership in Action&quot; feature
            in this client&apos;s Features panel to unlock
            transcript-based profiling.
          </p>
        </div>
      )
    }
    if (!hasIntegration) {
      return (
        <div className="p-30 border rounded bg-gray-light text-center">
          <p
            className="font-weight-bold mb-10"
            style={{ fontSize: 18 }}
          >
            Waiting on the client to connect a source
          </p>
          <p
            className="text-gray-dark mb-0"
            style={{ maxWidth: 540, margin: '0 auto' }}
          >
            Only the client can enter their own API key. Once
            they connect from their Leadership in Action page,
            you&apos;ll be able to generate profiles here.
          </p>
        </div>
      )
    }
    return (
      <div className="p-30 border rounded bg-gray-light text-center">
        <p
          className="font-weight-bold mb-10"
          style={{ fontSize: 18 }}
        >
          Generate this client&apos;s first profile
        </p>
        <p
          className="text-gray-dark mb-20"
          style={{ maxWidth: 540, margin: '0 auto' }}
        >
          Pick 3–10 of their recent meetings and we&apos;ll
          analyze how they showed up across 10 leadership
          competencies.
        </p>
        <Button
          variant="secondary"
          smallPadding
          onClick={openGenerateModal}
        >
          + Generate first profile
        </Button>
      </div>
    )
  }

  return (
    <div>
      {activeProfileId && (
        <div className="mb-30">
          <GenerationProgress
            profileId={activeProfileId}
            onStartOver={handleStartOver}
            onViewProfile={handleViewProfile}
          />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-15">
        <p
          className="font-weight-bold mb-0"
          style={{ fontSize: 18 }}
        >
          Profiles for this client
        </p>
        {hasIntegration && isFeatureEnabled && (
          <Button
            variant="secondary"
            smallPadding
            onClick={openGenerateModal}
          >
            + New profile
          </Button>
        )}
      </div>

      {profilesLoading ? (
        <p>Loading…</p>
      ) : hasProfiles ? (
        <Row>
          {profiles.map(profile => (
            <Col
              key={profile.id}
              xs={12}
              sm={6}
              lg={4}
              className="mb-20"
            >
              <ProfileCard
                profile={profile}
                onSelect={handleViewProfile}
                showGeneratedBy
              />
            </Col>
          ))}
        </Row>
      ) : (
        renderEmptyState()
      )}

      {renderConnectionFooter()}

      <GenerateProfileModal
        show={showGenerateModal}
        onClose={closeGenerateModal}
        providerName={
          activeProvider ? activeProvider.name : null
        }
        transcripts={transcripts}
        isLoading={transcriptsLoading}
        error={transcriptsError}
        onRetry={refetchTranscripts}
        onGenerate={handleGenerate}
        isGenerating={isCreating}
        generateError={createError}
      />
    </div>
  )
}

export default LeadershipInAction
